import React from 'react';
import { Card, Table } from '@avila-tek/ui/src';
import { useRouter } from 'next/router';
import { createColumnHelper } from '@tanstack/react-table';
import { Hit } from '@algolia/client-search';
import { Company, PageInfo, Pagination, User } from '../../models';
import { ActiveDotCell } from '../shared';
import { useAlgoliaSearch } from '../../hooks';
import { AlgoliaHitToObject } from '../../lib';
import { companyStatusTranslate } from '../../static/companyStatus';

const columnHelper = createColumnHelper<User>();

const columns = [
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore library has a issue: https://github.com/TanStack/table/issues/4224

  columnHelper.accessor('company', {
    header: 'Empresa',
    cell: (info) => `${(info.getValue() as Company)?.name ?? ''}`,
  }),

  columnHelper.accessor('dni', {
    header: 'RIF',
    cell: (info) => `J-${(info?.row.original.company as Company)?.dni}`,
  }),

  columnHelper.accessor('email', {
    header: 'Correo',
  }),

  columnHelper.display({
    header: 'Estatus',
    id: 'estatus',
    cell: (info) => (
      <ActiveDotCell
        active={(info.row.original.company as Company)?.status === 'active'}
        text={
          companyStatusTranslate[(info.row.original.company as Company)?.status]
        }
      />
    ),
  }),

  columnHelper.accessor('active', {
    header: 'Estado',
    cell: (info) => <ActiveDotCell active={info?.getValue()} />,
  }),
];

interface CompanyTableProps {
  data: Pagination<User>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function CompanyTable({ data, pagination, setPagination }: CompanyTableProps) {
  const router = useRouter();

  const [searchText, setSearchText] = React.useState<string | number>('');
  const [companiesData, setCompaniesData] = React.useState(data.items);
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<User>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'firstName',
      'lastName',
      'dni',
      'email',
      'company.name',
      'company.dni',
      'company.status',
    ],
    index: 'users',
    filters: `userType:company`,
  });

  React.useEffect(() => {
    async function syncData() {
      if (searchText) {
        const { pageInfo, items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
        });
        setCompaniesData(AlgoliaHitToObject<User>(items as Hit<User>[]));
        setPageInfoAlgolia(pageInfo);
      } else {
        setCompaniesData(data.items);
      }
    }
    syncData();
  }, [pageInfoAlgolia?.currentPage, searchText, pagination]);

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    const companyId = (
      data.items.find((user) => user._id === id).company as Company
    )._id;
    router.push({
      pathname: `/app/companies/${id}/${companyId}`,
      query: { tab: 'company' },
    });
  };

  const onClickAddButton = () => router.push('/app/companies/create');

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value });
    }
  };

  return (
    <Card className="overflow-x-auto p-5">
      <Table<User>
        data={companiesData}
        columns={columns}
        href="/app/company/create"
        manualPagination
      >
        <Table.Header className="p-6 w-full flex justify-between">
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          <Table.AddButton title="Compañia" action={onClickAddButton} />
        </Table.Header>
        <Table.Content<User> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: searchText
                ? {
                    ...pageInfoAlgolia,
                    currentPage: pageInfoAlgolia.currentPage + 1,
                  }
                : data.pageInfo,
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default CompanyTable;
